import select from 'dom-select';

import bodyEvents from './bodyEvents';

// Internal hostnames that should NOT be treated as external.
// No shared domain-config constant exists elsewhere in the codebase for these.
const INTERNAL_HOSTNAMES = ['cpb.bank', 'cpb.cloud.turn.work'];

const DIALOG_ID = 'link-interceptor';

// Populated on each external-link click, consumed on confirm/cancel.
let pendingHref = null;
let pendingTarget = null;

const isExternalHref = (href) => {
  if (!href || /^(mailto:|tel:|#|javascript:)/i.test(href)) {
    return false;
  }

  let url;
  try {
    url = new URL(href, window.location.origin);
  } catch {
    return false;
  }

  const hostname = url.hostname.toLowerCase();

  const isInternal = hostname === window.location.hostname.toLowerCase()
    || INTERNAL_HOSTNAMES.some((internalHostname) => {
      const lowered = internalHostname.toLowerCase();
      return hostname === lowered || hostname.endsWith(`.${lowered}`);
    });

  return !isInternal;
};

const getDialog = () => select(`#${DIALOG_ID}`, document);

const openExternalLinkDialog = () => {
  const dialog = getDialog();
  if (dialog) {
    dialog.showModal();
  }
};

const closeExternalLinkDialog = () => {
  const dialog = getDialog();
  if (dialog) {
    dialog.close();
  }
  pendingHref = null;
  pendingTarget = null;
};

const confirmNavigation = () => {
  if (!pendingHref) {
    return;
  }

  if (pendingTarget === '_blank') {
    window.open(pendingHref, '_blank', 'noopener,noreferrer');
  } else {
    window.location.href = pendingHref;
  }

  closeExternalLinkDialog();
};

const cancelNavigation = () => {
  closeExternalLinkDialog();
};

const bindDialogCloseReset = () => {
  const dialog = getDialog();
  if (dialog && !dialog.dataset.externalLinksBound) {
    dialog.addEventListener('close', () => {
      pendingHref = null;
      pendingTarget = null;
    });
    dialog.dataset.externalLinksBound = 'true';
  }
};

const handleDocumentClick = (event) => {
  const link = event.target.closest('a[rel="external"]');

  if (link) {
    // let native new-tab/new-window behavior happen for modifier/middle-clicks
    if (event.button === 1 || event.metaKey || event.ctrlKey || event.shiftKey) {
      return;
    }

    event.preventDefault();

    pendingHref = link.getAttribute('href');
    pendingTarget = link.getAttribute('target');

    openExternalLinkDialog();
    return;
  }

  const dialog = getDialog();
  if (dialog && dialog.contains(event.target)) {
    if (event.target.closest('.dialog__confirm')) {
      confirmNavigation();
    } else if (event.target.closest('.dialog__cancel, .dialog__close')) {
      cancelNavigation();
    }
  }
};

const getLinks = () => {
  // scoped to anchors only — [href] alone also matches <link rel="stylesheet">,
  // <base>, <area>, etc., which must never have their rel attribute touched
  select.all('a[href]', document).forEach((el) => {
    const currentHref = el.getAttribute('href');

    if (isExternalHref(currentHref)) {
      // gives users a visual indication the link is external and
      // should be announced as such by screenreaders
      el.setAttribute('rel', 'external');
    }
  });
};

const externalLinks = () => {
  bindDialogCloseReset();
  getLinks();
  bodyEvents.add('click', handleDocumentClick);
};

export default externalLinks;
